import { useEffect, useCallback } from 'react';
import { TOOLS } from '../utils/constants';

export function useKeyboard({
  selectTool,
  clearSelection,
  resetPending,
  resetDrawingTools, // NEU: Drawing-Tool-State zurücksetzen
  finishMeasure,
  finishPolyline, // NEU: Polyline mit Enter abschließen
  activeTool, // NEU: Aktuelles Tool
  deleteSelectedPoint,
  deleteSelectedWall,
  deleteSelectedSpace,
  deleteSelectedGeometry,
  explodeSelectedArc,
  selectedPointId,
  selectedWallId,
  selectedSpaceId,
  toggleOrtho,
  toggleGridSnap,
  toggleOsnap,
  undo,
  redo,
  // Transform-Funktionen
  startMove,
  startCopy,
  cancelTransform,
  isTransformActive,
  // Wall Volume functions
  createWallFromSelectedCurves,
  flipSelectedWall,
}) {
  const handleKeyDown = useCallback((e) => {
    // Ignorieren wenn in Input-Feld
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      return;
    }

    const key = e.key.toLowerCase();

    // Funktionstasten (F3, F8, F9 wie in Rhino/AutoCAD)
    switch (e.key) {
      case 'F3':
        e.preventDefault();
        toggleOsnap?.();
        return;
      case 'F8':
        e.preventDefault();
        toggleOrtho?.();
        return;
      case 'F9':
        e.preventDefault();
        toggleGridSnap?.();
        return;
    }

    // Tool-Shortcuts
    if (!e.ctrlKey && !e.metaKey) {
      // Shift shortcuts first
      if (e.shiftKey) {
        switch (key) {
          case 'w':
            // Shift+W: Create wall volume from selected curve(s)
            if (createWallFromSelectedCurves?.()) {
              e.preventDefault();
            }
            return;
          default:
            break;
        }
      }

      switch (key) {
        case 'v':
          selectTool(TOOLS.SELECT);
          break;
        case 'p':
          selectTool(TOOLS.POINT);
          break;
        case 'r':
          selectTool(TOOLS.SPACE);
          break;
        case 'w':
          // W ohne Shift: nichts (alte Wand entfernt). Shift+W: neue Wand.
          break;
        case 'f':
          // F für Flip (Wand Innen/Außen umdrehen)
          flipSelectedWall?.();
          break;
        case 'a':
          selectTool(TOOLS.ARC);
          break;
        case 'b':
          // B für Box (Quader)
          selectTool(TOOLS.BOX);
          break;
        case 'j':
          // J für Cylinder (Zylinder)
          selectTool(TOOLS.CYLINDER);
          break;
        case 'u':
          // U für Sphere (kUgel)
          selectTool(TOOLS.SPHERE);
          break;
        case 'l':
          // L für Line (Linie)
          selectTool(TOOLS.LINE);
          break;
        case 'n':
          // N für polyliNe (Polylinie)
          selectTool(TOOLS.POLYLINE);
          break;
        case 'k':
          // K für Kreis (Circle)
          selectTool(TOOLS.CIRCLE);
          break;
        case 'e':
          // E für rEctangle (Rechteck)
          selectTool(TOOLS.RECTANGLE);
          break;
        case 'y':
          // Y für polYgon
          selectTool(TOOLS.POLYGON);
          break;
        case 'h':
          // H für Hilfslinie (Construction Line)
          selectTool(TOOLS.CONSTRUCTION_LINE);
          break;
        case 'm':
          // M für Move (Verschieben) - wie in Rhino
          if (startMove) {
            startMove();
          }
          break;
        case 'c':
          // C für Copy (Kopieren) - wie in Rhino
          if (startCopy) {
            startCopy();
          }
          break;
        case 'i':
          // I für Inspect/Measure (Messen)
          selectTool(TOOLS.MEASURE);
          break;
        case 'o':
          // O für Ortho (alternativ zu F8)
          toggleOrtho?.();
          break;
        case 'g':
          // G für Grid Snap (alternativ zu F9)
          toggleGridSnap?.();
          break;
        case 's':
          // S für Snap (alternativ zu F3)
          if (!e.ctrlKey && !e.metaKey) {
            toggleOsnap?.();
          }
          break;
        case 'escape':
          // ESC bricht alle aktiven Befehle ab und wechselt zu SELECT
          if (isTransformActive && cancelTransform) {
            cancelTransform();
          }
          // Immer aufräumen
          clearSelection?.();
          resetPending?.();
          resetDrawingTools?.();
          // Zurück zum SELECT-Tool
          selectTool(TOOLS.SELECT);
          break;
        case 'enter':
          // Enter zum Abschließen von Messungen oder Polylines
          if (activeTool === TOOLS.POLYLINE && finishPolyline) {
            finishPolyline();
          } else if (activeTool === TOOLS.MEASURE && finishMeasure) {
            finishMeasure();
          }
          break;
        case 'delete':
        case 'backspace':
          // Try geometry first, then building elements
          if (deleteSelectedGeometry?.()) {
            // Geometry was deleted
          } else if (selectedPointId && deleteSelectedPoint) {
            deleteSelectedPoint();
          } else if (selectedWallId && deleteSelectedWall) {
            deleteSelectedWall();
          } else if (selectedSpaceId && deleteSelectedSpace) {
            deleteSelectedSpace();
          }
          break;
        case 'x':
          // X für Explode (Bogen in Einzelsegmente auflösen)
          if (explodeSelectedArc) {
            explodeSelectedArc();
          }
          break;
        default:
          break;
      }
    }

    // Ctrl/Cmd Shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (key) {
        case 'z':
          if (e.shiftKey) {
            redo?.();
          } else {
            undo?.();
          }
          e.preventDefault();
          break;
        case 'y':
          redo?.();
          e.preventDefault();
          break;
        default:
          break;
      }
    }
  }, [
    selectTool,
    clearSelection,
    resetPending,
    resetDrawingTools,
    finishMeasure,
    deleteSelectedPoint,
    deleteSelectedWall,
    deleteSelectedSpace,
    deleteSelectedGeometry,
    explodeSelectedArc,
    selectedPointId,
    selectedWallId,
    selectedSpaceId,
    toggleOrtho,
    toggleGridSnap,
    toggleOsnap,
    undo,
    redo,
    startMove,
    startCopy,
    cancelTransform,
    isTransformActive,
    createWallFromSelectedCurves,
    flipSelectedWall,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
